import { GraphicPrimitive } from "./GraphicPrimitive";


export class Spline extends GraphicPrimitive {
    constructor(points, color, fillColor, fillType, spacing, angle, segmentLength) {
        super(color, fillColor, fillType, spacing, angle, segmentLength, 'simple', 'Spline');
        this.points = points;
        this.regenerateStitches();
    }

    toJSON() {
        return {
            ...super.toJSON(),
            points: this.points
        };
    }

    getControlPoints() {
        return this.points;
    }

    updateControlPoint(pointIndex, newX, newY) {
        console.log(`Spline: Updating control point ${pointIndex} to (${newX}, ${newY})`);
        if (pointIndex >= 0 && pointIndex < this.points.length) {
            this.points[pointIndex].x = newX;
            this.points[pointIndex].y = newY;
            this.regenerateStitches();
        }
    }

    move(dx, dy) {
        const isClosed = this.isClosed();
        const numPointsToMove = isClosed ? this.points.length - 1 : this.points.length;

        for (let i = 0; i < numPointsToMove; i++) {
            let p = this.points[i];
            p.x += dx;
            p.y += dy;
        }
        // If closed, update the last point to match the first point after moving
        if (isClosed) {
            this.points[this.points.length - 1].x = this.points[0].x;
            this.points[this.points.length - 1].y = this.points[0].y;
        }
        this.regenerateStitches();
    }

    isClosed() {
        return this.points.length > 2 &&
            this.points[0].x === this.points[this.points.length - 1].x &&
            this.points[0].y === this.points[this.points.length - 1].y;
    }

    toPolygon() {
        // Convert spline to polygon format by sampling the quadratic curve
        if (this.points.length < 3) return [];
        if (!this.isClosed()) return []; // Only generate for closed splines

        const sampledPoints = [];
        const renderPoints = this.points.slice(0, -1);
        const n = renderPoints.length;
        const samplesPerSegment = 10;

        for (let i = 0; i < n; i++) {
            const p1 = renderPoints[i];
            const p2 = renderPoints[(i + 1) % n];
            const p3 = renderPoints[(i + 2) % n];

            const mid1 = {
                x: (p1.x + p2.x) / 2,
                y: (p1.y + p2.y) / 2,
            };
            const mid2 = {
                x: (p2.x + p3.x) / 2,
                y: (p2.y + p3.y) / 2,
            };

            for (let t = 0; t < samplesPerSegment; t++) {
                const ratio = t / samplesPerSegment;
                const mt = 1 - ratio;
                // Quadratic Bézier formula: (1-t)^2 * P0 + 2(1-t)t * P1 + t^2 * P2
                // Here, P0 = mid1, P1 = p2, P2 = mid2
                const x = mt * mt * mid1.x + 2 * mt * ratio * p2.x + ratio * ratio * mid2.x;
                const y = mt * mt * mid1.y + 2 * mt * ratio * p2.y + ratio * ratio * mid2.y;
                sampledPoints.push([x, y]);
            }
        }

        return [[[...sampledPoints, [sampledPoints[0][0], sampledPoints[0][1]]]]];
    }

    regenerateStitches() {
        const multiPoly = this.toPolygon();
        const parallelStitches = [];

        if (this.fillColor && this.fillType === 'parallel' && this.isClosed()) {
            const minX = Math.min(...this.points.map(p => p.x));
            const maxX = Math.max(...this.points.map(p => p.x));
            const minY = Math.min(...this.points.map(p => p.y));
            const maxY = Math.max(...this.points.map(p => p.y));
            const bounds = { minX, maxX, minY, maxY };

            this.generateParallelStitches(bounds, multiPoly);
            if (this.stitchLists.length > 0) {
                this.stitchLists.forEach(list => {
                    list.color = this.fillColor;
                    parallelStitches.push(list);
                });
            }
        }

        const contourStitches = this.generateContourStitches(multiPoly);
        this.stitchLists = [...parallelStitches, ...contourStitches];
    }

    AddPoint(point) {
        if (this.points.length > 0) {
            const dist = Math.sqrt(Math.pow(point.x - this.points[0].x, 2) + Math.pow(point.y - this.points[0].y, 2));
            if (dist < 10) {
                this.points.push(this.points[0]);
                this.regenerateStitches(); // Generate stitches when shape is closed
                return true;
            }
        }
        this.points.push(point);
        return false;
    }

    mouseUp() {
        return true;

    }

    renderDrawing(ctx, transform) {
        if (this.points.length < 2) return;

        const isClosed = this.points.length > 2 && this.points[0].x === this.points[this.points.length - 1].x && this.points[0].y === this.points[this.points.length - 1].y;

        const minX = Math.min(...this.points.map(p => p.x));
        const maxX = Math.max(...this.points.map(p => p.x));
        const minY = Math.min(...this.points.map(p => p.y));
        const maxY = Math.max(...this.points.map(p => p.y));
        const bounds = { minX, maxX, minY, maxY };

        const drawSplinePath = () => {
            if (isClosed) {
                const renderPoints = this.points.slice(0, -1);
                const n = renderPoints.length;
                if (n < 2) return;

                const firstMid = {
                    x: (renderPoints[n - 1].x + renderPoints[0].x) / 2,
                    y: (renderPoints[n - 1].y + renderPoints[0].y) / 2,
                };
                ctx.moveTo(firstMid.x, firstMid.y);

                for (let i = 0; i < n; i++) {
                    const p1 = renderPoints[i];
                    const p2 = renderPoints[(i + 1) % n];
                    const mid = {
                        x: (p1.x + p2.x) / 2,
                        y: (p1.y + p2.y) / 2,
                    };
                    ctx.quadraticCurveTo(p1.x, p1.y, mid.x, mid.y);
                }
                ctx.closePath();
            } else {
                ctx.moveTo(this.points[0].x, this.points[0].y);
                for (let i = 1; i < this.points.length - 1; i++) {
                    const midPoint = {
                        x: (this.points[i].x + this.points[i + 1].x) / 2,
                        y: (this.points[i].y + this.points[i + 1].y) / 2,
                    };
                    ctx.quadraticCurveTo(this.points[i].x, this.points[i].y, midPoint.x, midPoint.y);
                }
                ctx.lineTo(this.points[this.points.length - 1].x, this.points[this.points.length - 1].y);
            }
        };

        // Bounds tolerance for control point overshoot (quadratic doesn't overshoot much but safe to add)
        const tolerance = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY) * 0.1 + 20;
        bounds.minX -= tolerance;
        bounds.maxX += tolerance;
        bounds.minY -= tolerance;
        bounds.maxY += tolerance;

        if (isClosed) {
            this.drawFill(ctx, transform, drawSplinePath, bounds);
        }

        // Always draw stitches if they exist
        this.drawStitches(ctx, transform);

        ctx.beginPath();
        drawSplinePath();

        // Manual fill removed, handled by drawFill
        // if (isClosed && this.fillStyle) { ... }

        ctx.strokeStyle = this.isSelected ? 'red' : this.color;
        ctx.lineWidth = this.lineWidth / transform.scale;
        ctx.stroke();
    }

    drawPoint(ctx, x, y, color, transform) {
        ctx.beginPath();
        const radius = 5 / transform.scale;
        ctx.arc(x, y, radius, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.fill();
    }

    renderActivePoints(ctx, transform) {
        if (this.points)
            this.points.forEach(p => this.drawPoint(ctx, p.x, p.y, 'red', transform));

    }

    static renderPreview(ctx, splinePoints, color, transform) {
        const LineWidth = 1;
        if (splinePoints.length < 2) return;
        ctx.beginPath();
        ctx.moveTo(splinePoints[0].x, splinePoints[0].y);
        for (let i = 1; i < splinePoints.length - 1; i++) {
            const midPoint = {
                x: (splinePoints[i].x + splinePoints[i + 1].x) / 2,
                y: (splinePoints[i].y + splinePoints[i + 1].y) / 2,
            };
            ctx.quadraticCurveTo(splinePoints[i].x, splinePoints[i].y, midPoint.x, midPoint.y);
        }
        ctx.quadraticCurveTo(
            splinePoints[splinePoints.length - 1].x,
            splinePoints[splinePoints.length - 1].y,
            splinePoints[splinePoints.length - 1].x,
            splinePoints[splinePoints.length - 1].y
        );
        ctx.strokeStyle = this.isSelected ? 'red' : this.color;
        ctx.lineWidth = LineWidth / transform.scale;
        ctx.stroke();

    }

    isPointInside(x, y, transform) {
        if (this.points.length < 2) return false;

        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        const path = new Path2D();

        const isClosed = this.points.length > 2 && this.points[0].x === this.points[this.points.length - 1].x && this.points[0].y === this.points[this.points.length - 1].y;

        if (isClosed) {
            const renderPoints = this.points.slice(0, -1);
            const n = renderPoints.length;
            if (n < 2) return false;

            const firstMid = { x: (renderPoints[n - 1].x + renderPoints[0].x) / 2, y: (renderPoints[n - 1].y + renderPoints[0].y) / 2 };
            path.moveTo(firstMid.x, firstMid.y);

            for (let i = 0; i < n; i++) {
                const p1 = renderPoints[i];
                const p2 = renderPoints[(i + 1) % n];
                const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
                path.quadraticCurveTo(p1.x, p1.y, mid.x, mid.y);
            }
            path.closePath();
        } else {
            path.moveTo(this.points[0].x, this.points[0].y);
            for (let i = 1; i < this.points.length - 1; i++) {
                const midPoint = { x: (this.points[i].x + this.points[i + 1].x) / 2, y: (this.points[i].y + this.points[i + 1].y) / 2 };
                path.quadraticCurveTo(this.points[i].x, this.points[i].y, midPoint.x, midPoint.y);
            }
            path.lineTo(this.points[this.points.length - 1].x, this.points[this.points.length - 1].y);
        }

        // Set a wider line width for hit testing
        tempCtx.lineWidth = (this.lineWidth + 10) / transform.scale; // Add some tolerance
        return tempCtx.isPointInStroke(path, x, y);
    }


    processMouseEvent(eventType, event) {
        // Optionally implement mouse event processing logic
    }
}






